import React from "react";

import { newRelicPageAction } from "@jslib/shared/utils/logger";

const CAPTCHA_PAGE_ACTION = "Captcha";

const createCaptchaMetrics = () => ({
  rendered: false,
  verified: false,
  retryCount: 0,
  serverError: false,
  clientErrorCodes: [] as number[],
  clientErrorVisibilityState: "",
  scriptLoadError: false,
  browserUnsupported: false,
  loadTimeout: false,
});

type CaptchaMetrics = ReturnType<typeof createCaptchaMetrics>;

const deriveOutcome = (m: CaptchaMetrics): string => {
  if (m.verified) return "verified";
  if (m.browserUnsupported) return "browser-unsupported";
  if (m.scriptLoadError) return "script-load-error";
  if (m.loadTimeout) return "load-timeout";
  if (m.clientErrorCodes.length) return "client-error";
  if (m.serverError) return "server-error";
  return m.rendered ? "abandoned-after-render" : "unmounted-before-render";
};

interface UseCaptchaMetrics {
  markRendered: () => void;
  markVerified: () => void;
  incrementRetryCount: () => void;
  markServerError: () => void;
  /** Adds a unique client error code and captures tab visibility on the first error of the session. */
  recordClientError: (code: number) => void;
  markScriptLoadError: () => void;
  markBrowserUnsupported: () => void;
  /** No-op if the script never loaded because a script-load error already covers that case. */
  markLoadTimeout: () => void;
  /** Send the accumulated outcome as a single New Relic pageAction. Fires at most once. */
  emit: () => void;
}

/**
 * Accumulates the captcha lifecycle into a single outcome and sends it to NR.
 */
export const useCaptchaMetrics = ({
  segment,
  programId,
}: {
  segment?: string;
  programId?: string;
}): UseCaptchaMetrics => {
  const metrics = React.useRef<CaptchaMetrics>(createCaptchaMetrics());
  const hasEmitted = React.useRef(false);

  return {
    markRendered: () => {
      metrics.current.rendered = true;
    },
    markVerified: () => {
      metrics.current.verified = true;
    },
    incrementRetryCount: () => {
      metrics.current.retryCount += 1;
    },
    markServerError: () => {
      metrics.current.serverError = true;
    },
    recordClientError: (code: number) => {
      if (metrics.current.clientErrorCodes.length === 0) {
        // Captures whether the tab was backgrounded when the captcha first errored.
        metrics.current.clientErrorVisibilityState = document.visibilityState;
      }
      if (!metrics.current.clientErrorCodes.includes(code)) {
        metrics.current.clientErrorCodes.push(code);
      }
    },
    markScriptLoadError: () => {
      metrics.current.scriptLoadError = true;
    },
    markBrowserUnsupported: () => {
      metrics.current.browserUnsupported = true;
    },
    markLoadTimeout: () => {
      if (!metrics.current.scriptLoadError) {
        metrics.current.loadTimeout = true;
      }
    },
    emit: () => {
      if (hasEmitted.current) {
        return;
      }
      hasEmitted.current = true;
      newRelicPageAction(CAPTCHA_PAGE_ACTION, {
        outcome: deriveOutcome(metrics.current),
        ...metrics.current,
        // flatten array to a string for NR
        clientErrorCodes: metrics.current.clientErrorCodes.join(","),
        segment,
        programId,
      });
    },
  };
};
